import { forwardRef } from 'react'

import ChevronDownIcon from '@heroicons/react/outline/ChevronDownIcon'
import loadable from '@loadable/component'
import { Prompt } from 'react-router'
import FontSizeIcon from 'remixicon-react/FontSizeIcon'
import Image2LineIcon from 'remixicon-react/Image2LineIcon'

import { Member, Space } from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { ErrorBoundary } from '../Error/ErrorBoundry'
import { SelectableTag, SpaceTagsSelector } from '../Space/SpaceTagsSelector'
import { Attachment } from '../TipTap/@types'
import { Attachments } from '../TipTap/components/Attachment/Attachments'
import { AttachmentPickerButton } from '../TipTap/components/AttachmentPickerButton'
import { ComposerIconButton } from '../TipTap/components/ComposerIconButton'
import { EmojiPickerButton } from '../TipTap/components/EmojiPickerButton'
import { SlashMenuButton } from '../TipTap/components/SlashMenuButton'
import { TextFormattingMenu } from '../TipTap/components/TextBubbleMenu'
import { useTiptap } from '../TipTap/hooks/useTiptap'
import { PostComposerActiveView } from './@types'

const Tiptap = loadable(() => import('../TipTap'), {
  resolveComponent: module => module.Tiptap,
  ssr: false,
})

export type ComposerModalContentProps = {
  space: Space
  title: string
  titleError: string
  setTitle: (title: string) => void
  setActiveView: (view: PostComposerActiveView) => void
  tags: SelectableTag[]
  setTags: (tags: SelectableTag[]) => void
  attachments: Attachment[]
  handleAttachmentDelete: (attachment: Attachment) => void
  loading: boolean
  owner: Member
  save: () => void
  isNew: boolean
}

export const ComposerModalContent = forwardRef<
  HTMLInputElement,
  ComposerModalContentProps
>(
  (
    {
      space,
      title,
      setTitle,
      titleError,
      tags,
      setTags,
      attachments,
      handleAttachmentDelete,
      loading,
      owner,
      save,
      setActiveView,
      isNew,
    },
    ref,
  ) => {
    const {
      onFileUpload,
      isTouched,
      editor,
      toggleDialog,
      onSlashMenuItemClick,
      state,
    } = useTiptap()
    const { data: authMember } = useAuthMember()
    const postMember = owner || authMember

    return (
      <>
        {/* This "id" attr is being used to cover an issue with the headless-ui modals
         ** Where clicking on a dropdown consider as clicking outside of the modal so we append the dropdown portals used in tiptap to this modal
         */}
        <Modal.Content
          id="tiptap-editor-wrapper"
          className="w-full max-w-2xl mx-auto overflow-auto"
        >
          <div className="flex flex-1 space-x-4 items-center overflow-hidden">
            <div className="flex-shrink-0">
              <Avatar
                name={postMember?.name}
                size="lg"
                src={
                  postMember?.profilePicture &&
                  'urls' in postMember?.profilePicture
                    ? postMember?.profilePicture?.urls?.thumb
                    : undefined
                }
              />
            </div>
            <div className="overflow-hidden flex-grow -mb-1">
              <div className="truncate max-w-full flex items-center text-basicSurface-900 font-medium">
                {postMember.name}
              </div>
              <div className="text-sm h-7 text-basicSurface-500 truncate">
                {isNew ? (
                  <div className="flex items-center">
                    <div className="mr-1">Posting in</div>
                    <Badge
                      as={Link}
                      onClick={() => {
                        setActiveView('spacePicker')
                      }}
                      variant="secondary"
                      trailingIcon={<ChevronDownIcon />}
                      size="lg"
                    >
                      {space?.name}
                    </Badge>
                  </div>
                ) : (
                  <span className="font-medium">Posted in {space?.name}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col overflow-hidden w-full">
            <input
              ref={ref}
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Post title"
              className="block text-2xl w-full mt-2 py-2 bg-transparent appearance-none focus:outline-none text-basicSurface-500 placeholder-basicSurface-300/75"
            />
            {titleError && (
              <p className="-mt-2 font-medium text-danger-600">{titleError}</p>
            )}
            <ErrorBoundary>
              <div
                // This hack is here because headless modal controls the keypress and steals the focus out of the editor
                onKeyDown={e => {
                  const el = document.getElementById('tiptap-editor-wrapper')
                  if (
                    e.key === 'Tab' &&
                    e.target instanceof Node &&
                    el?.contains(e.target)
                  ) {
                    e.preventDefault()
                    e.stopPropagation()
                  }
                }}
                style={{ minHeight: '160px' }} // min-h-40 not working?!
                className="relative my-2 overflow-y-auto flex flex-col flex-grow"
              >
                <Tiptap>
                  {!!attachments?.length && (
                    <div className="pb-5">
                      <Attachments
                        attachments={attachments}
                        onAttachmentDelete={handleAttachmentDelete}
                      />
                    </div>
                  )}
                </Tiptap>
                <Prompt
                  when={isTouched}
                  message={() => 'Changes you made may not be saved.'}
                />
              </div>
            </ErrorBoundary>
          </div>
        </Modal.Content>
        <Modal.Footer className="w-full max-w-2xl mx-auto">
          <SpaceTagsSelector
            className="relative mb-2"
            spaceId={space?.id}
            tags={tags}
            setTags={setTags}
            placement="top-start"
          />
          {editor && state.textFormatMenu && (
            <TextFormattingMenu
              editor={editor}
              className="mb-2 shadow-none border-none px-0"
            />
          )}
          <div className="justify-between flex items-center">
            {editor && (
              <div className="flex space-x-2 items-center">
                <SlashMenuButton
                  size="sm"
                  onItemClick={props => {
                    setTimeout(() => {
                      onSlashMenuItemClick({
                        editor,
                        props,
                      })
                    }, 100)
                  }}
                />
                <ComposerIconButton
                  icon={FontSizeIcon}
                  aria-label="text formatting"
                  size="sm"
                  onClick={() => {
                    toggleDialog('textFormatMenu', !state.textFormatMenu)
                  }}
                  className={
                    state.textFormatMenu
                      ? 'bg-surface-100 hover:bg-surface-200'
                      : ''
                  }
                />

                <div className="hidden sm:flex space-x-2 items-center">
                  <EmojiPickerButton size="sm" editor={editor} />
                  <ComposerIconButton
                    size="sm"
                    icon={Image2LineIcon}
                    aria-label="image"
                    onClick={() => {
                      toggleDialog('image', true)
                    }}
                  />
                  <AttachmentPickerButton
                    size="sm"
                    onAttachmentSelect={files =>
                      onFileUpload(files, { asAttachment: true })
                    }
                  />
                </div>
              </div>
            )}
            <Button
              size="md"
              className="ml-auto text-white"
              loading={loading}
              onClick={save}
            >
              {isNew ? 'Publish' : 'Update'}
            </Button>
          </div>
        </Modal.Footer>
      </>
    )
  },
)
